import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { subscribeToTier } from '../../Tier/redux/consumer/actions';
import { isConsumerReduxReady, isMatureFilterOverridden } from '../../Tier/redux/consumer/selectors';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import { DeviationResourceType } from '../../types/deviation';
import { useRestrictToLoggedIn } from '../../utils/hooks/useRestrictToLoggedIn';
import { formatPrice } from '../../DeviationViews/TierProductCardDumb/utils';
export function useSubscribeButton(tier, { type, matureFilterDisabled, referral_info, showUnsubAsView, withPrice, href: hrefProp } = {}) {
    const waitingAction = useRef();
    const [loadRedux, setLoadRedux] = useState(false);
    const { t } = useTranslation();
    const { showBlocked } = useDeviationMatureFilter(tier);
    const reduxReady = useSelector(isConsumerReduxReady);
    const matureFilterOverridden = useSelector((state)=>{
        var _tier;
        return isMatureFilterOverridden(state, (_tier = tier) === null || _tier === void 0 ? void 0 : _tier.deviationId);
    });
    const dispatch = useDispatch();
    const { restrictToLoggedIn } = useRestrictToLoggedIn();
    const isMatureBlocked = showBlocked && !matureFilterDisabled && !matureFilterOverridden;
    const locked_to_sub_page_flag = useFlag('locked_to_sub_page');
    useEffect(()=>{
        if (reduxReady && waitingAction.current) {
            waitingAction.current();
            waitingAction.current = undefined;
        }
    }, [
        reduxReady
    ]);
    useEffect(()=>{
        if (isMatureBlocked) {
            // mature blocked shows disabled, so we cannot wait for a click
            // to load the redux stuff
            setLoadRedux(true);
        }
    }, [
        isMatureBlocked
    ]);
    const variant = useMemo(()=>{
        var _tier_tier, _tier, _tier_tier1, _tier1;
        if (!tier) {
            return 'disabled';
        }
        if (type) {
            return type;
        }
        if ((_tier = tier) === null || _tier === void 0 ? void 0 : (_tier_tier = _tier.tier) === null || _tier_tier === void 0 ? void 0 : _tier_tier.isUserSubscribed) {
            if (showUnsubAsView) {
                return 'view';
            }
            return 'subscribed';
        }
        if (isMatureBlocked || !((_tier1 = tier) === null || _tier1 === void 0 ? void 0 : (_tier_tier1 = _tier1.tier) === null || _tier_tier1 === void 0 ? void 0 : _tier_tier1.canUserSubscribe)) {
            return 'disabled';
        }
        return 'subscribe';
    }, [
        isMatureBlocked,
        showUnsubAsView,
        tier,
        type
    ]);
    const subscribe = useCallback((e)=>{
        if (!tier) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        if (locked_to_sub_page_flag) {
            window.location.href = Url.userSubscriptionsPath({
                username: tier.author.username,
                tierDeviationId: tier.deviationId
            });
            return;
        }
        if (!reduxReady) {
            waitingAction.current = ()=>{
                dispatch(subscribeToTier(tier));
            };
            setLoadRedux(true);
            return;
        }
        dispatch(subscribeToTier(tier));
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        dispatch,
        reduxReady,
        tier,
        locked_to_sub_page_flag
    ]);
    const { onClick, href, biData, text, disabled } = useMemo(()=>{
        switch(variant){
            case 'subscribe':
                var _tier_tier, _tier, _tier_tier1, _tier1;
                return {
                    onClick: restrictToLoggedIn(ReferralType.MEMBER_SUBSCRIBE, subscribe),
                    href: hrefProp,
                    biData: BiData({
                        evid: 550,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId,
                        referral_info: referral_info !== null && referral_info !== void 0 ? referral_info : BiContextProvidedValue
                    }),
                    text: withPrice && ((_tier = tier) === null || _tier === void 0 ? void 0 : (_tier_tier = _tier.tier) === null || _tier_tier === void 0 ? void 0 : _tier_tier.dollarPrice) ? t('deviation.tier.subscribeWithPrice', {
                        dollarPrice: formatPrice((_tier1 = tier) === null || _tier1 === void 0 ? void 0 : (_tier_tier1 = _tier1.tier) === null || _tier_tier1 === void 0 ? void 0 : _tier_tier1.dollarPrice)
                    }) : t('deviation.tier.subscribe'),
                    reduxReady
                };
            case 'view':
                var _tier2;
                return {
                    href: Url.userSubscriptionsPath({
                        username: tier.author.username,
                        tierDeviationId: (_tier2 = tier) === null || _tier2 === void 0 ? void 0 : _tier2.deviationId,
                        tierContentType: 'deviations'
                    }),
                    biData: BiData({
                        evid: 544,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId
                    }),
                    text: t('deviation.tier.viewSubscription'),
                    reduxReady
                };
            case 'subscribed':
                return {
                    href: hrefProp,
                    biData: BiData({
                        evid: 551,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId,
                        referral_info: (typeof window !== 'undefined' && window.location.href === URLS.ORDERS ? ReferralType.BILLING_PAYMENTS : ReferralType.TIER_CARD) || ReferralType.TIER_CARD
                    }),
                    text: t('deviation.tier.subscribed'),
                    reduxReady
                };
            case 'disabled':
                return {
                    text: t('deviation.tier.noSubscribing'),
                    disabled: true,
                    reduxReady
                };
            default:
                return {};
        }
    }, [
        hrefProp,
        referral_info,
        restrictToLoggedIn,
        subscribe,
        t,
        tier,
        variant,
        withPrice,
        reduxReady
    ]);
    return {
        loadRedux,
        variant,
        onClick,
        href,
        biData,
        text,
        disabled
    };
}
